import { useDroppable } from "@dnd-kit/core";
import { isSameDay, addHours } from "date-fns";
import { cn } from "@/lib/utils";
import { OrderWithRelations } from "./types";
import { Tractor, Location, WorkOrder } from "@/types/tms";
import { DraggableAssetOrderCard } from "./DraggableAssetOrderCard";
import { WorkOrderCard } from "./WorkOrderCard";
import { AssetLocationCard } from "./AssetLocationCard";
import { Truck, Wrench } from "lucide-react";
import type { TractorServiceStatus } from "@/lib/tractor-service-status";

interface DroppableAssetTimelineRowProps {
  tractor: Tractor & { home_base: Location | null };
  orders: OrderWithRelations[];
  workOrders: WorkOrder[];
  weekStart: Date;
  weekEnd: Date;
  weekDays: Date[];
  onOrderClick?: (order: OrderWithRelations) => void;
  onCreateWorkOrder?: (tractor: Tractor & { home_base: Location | null }) => void;
  onTractorClick?: (tractor: Tractor & { home_base: Location | null }) => void;
  /** Active OOS windows for this tractor. Rendered as a muted diagonal-
   *  striped band on the timeline. */
  oosWindows?: Array<{ start: Date; end: Date | null }>;
  /** Current derived status for this tractor. When present and not
   *  "available", a small OOS chip is rendered beside the name. */
  oosStatus?: TractorServiceStatus;
}

interface TimelineSegment {
  type: "in_transit" | "at_location" | "work_order";
  startTime: Date;
  endTime: Date | null;
  order?: OrderWithRelations;
  workOrder?: WorkOrder;
  locationName?: string;
  isAtHome?: boolean;
}

import { differenceInMinutes } from "date-fns";

export function DroppableAssetTimelineRow({
  tractor,
  orders,
  workOrders,
  weekStart,
  weekEnd,
  weekDays,
  onOrderClick,
  onCreateWorkOrder,
  onTractorClick,
  oosWindows,
  oosStatus,
}: DroppableAssetTimelineRowProps) {
  const TOTAL_WEEK_MINUTES = Math.max(differenceInMinutes(weekEnd, weekStart), 1);
  const { setNodeRef, isOver } = useDroppable({
    id: `tractor-${tractor.id}`,
    data: {
      type: "tractor",
      tractorId: tractor.id,
      tractorName: tractor.name,
    },
  });

  const homeBaseCity = tractor.home_base?.city || null;
  const isUnassignedRow = tractor.id === "unassigned";
  const segments = isUnassignedRow
    ? orders.map((o) => ({
        type: "in_transit" as const,
        startTime: new Date(o.ready_time),
        endTime: new Date(o.requested_delivery_time_end ?? o.requested_delivery_time),
        order: o,
      }))
    : buildTimelineSegments(orders, workOrders, weekStart, weekEnd, homeBaseCity);

  return (
    <div className="flex border-b last:border-b-0">
      {/* Tractor name panel — sticky so it stays visible during horizontal scroll */}
      <button
        type="button"
        onClick={() => {
          if (isUnassignedRow) return;
          onTractorClick?.(tractor);
        }}
        disabled={isUnassignedRow || !onTractorClick}
        className={cn(
          "w-[200px] shrink-0 p-3 border-r bg-background sticky left-0 z-20 text-left flex flex-col justify-center",
          !isUnassignedRow && onTractorClick && "hover:bg-accent transition-colors cursor-pointer",
        )}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {!isUnassignedRow && <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
          <span className={cn("font-medium text-sm truncate", isUnassignedRow && "text-muted-foreground")}>
            {tractor.name}
          </span>
          {!isUnassignedRow && oosStatus && oosStatus !== "available" && (
            <span
              className={cn(
                "ml-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium shrink-0",
                oosStatus === "upcoming_service"
                  ? "bg-muted text-muted-foreground"
                  : "bg-destructive/15 text-destructive",
              )}
              title={oosStatus.split("_").join(" ")}
            >
              <Wrench className="h-2.5 w-2.5" />
              OOS
            </span>
          )}
        </div>
        {!isUnassignedRow && (
          <div className="flex items-center gap-1 mt-1">
            {tractor.capabilities && tractor.capabilities.length > 0 ? (
              tractor.capabilities.map((cap) => (
                <span
                  key={cap}
                  className={cn(
                    "px-1.5 py-0.5 text-[10px] font-medium rounded",
                    cap === "VO" && "bg-[hsl(var(--status-delivered)/0.15)] text-[hsl(var(--status-delivered))]",
                    cap === "NVO" && "bg-[hsl(var(--status-confirmed)/0.15)] text-[hsl(var(--status-confirmed))]",
                    cap === "Manual" && "bg-[hsl(var(--status-in-transit)/0.15)] text-[hsl(var(--status-in-transit))]"
                  )}
                >
                  {cap}
                </span>
              ))
            ) : null}
          </div>
        )}
      </button>

      {/* Timeline area - droppable */}
      <div
        ref={setNodeRef}
        className={cn(
          "relative flex-1 min-h-[72px] transition-colors",
          isOver && "bg-primary/10"
        )}
      >
        {/* OOS window bands — non-interactive, sit below cards. */}
        {!isUnassignedRow && oosWindows && oosWindows.length > 0 && (
          <div className="absolute inset-0 pointer-events-none">
            {oosWindows.map((w, i) => {
              const startMs = Math.max(w.start.getTime(), weekStart.getTime());
              const endMs = Math.min(
                (w.end ?? weekEnd).getTime(),
                weekEnd.getTime(),
              );
              if (endMs <= startMs) return null;
              const leftPct =
                ((startMs - weekStart.getTime()) / 60_000 / TOTAL_WEEK_MINUTES) * 100;
              const widthPct =
                ((endMs - startMs) / 60_000 / TOTAL_WEEK_MINUTES) * 100;
              return (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 bg-destructive/10"
                  style={{
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    backgroundImage:
                      "repeating-linear-gradient(45deg, hsl(var(--destructive) / 0.15) 0 6px, transparent 6px 12px)",
                  }}
                  title="Out of service"
                />
              );
            })}
          </div>
        )}

        {/* Day column backgrounds */}
        <div className="absolute inset-0 flex pointer-events-none">
          {weekDays.map((day, index) => (
            <div
              key={day.toISOString()}
              className={cn(
                "flex-1 min-w-[180px] border-r last:border-r-0 relative",
                index % 2 === 1 && "bg-muted/20",
                isSameDay(day, new Date()) && "bg-row-selected"
              )}
            >
              {weekDays.length === 1 && (
                <div className="absolute inset-y-0 inset-x-0 flex">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex-1 border-l border-border/40",
                        i === 0 && "border-l-0",
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Render segments */}
        <div className="relative h-full">
          {segments.map((segment, index) => {
            if (segment.type === "in_transit" && segment.order) {
              return (
                <DraggableAssetOrderCard
                  key={`order-${segment.order.id}`}
                  order={segment.order}
                  weekStart={weekStart}
                  totalMinutes={TOTAL_WEEK_MINUTES}
                  onClick={() => onOrderClick?.(segment.order!)}
                />
              );
            } else if (segment.type === "work_order" && segment.workOrder) {
              return (
                <WorkOrderCard
                  key={`work-order-${segment.workOrder.id}`}
                  workOrder={segment.workOrder}
                  tractor={tractor}
                  weekStart={weekStart}
                  totalMinutes={TOTAL_WEEK_MINUTES}
                />
              );
            } else if (segment.type === "at_location" && segment.locationName && !segment.isAtHome) {
              return (
                <AssetLocationCard
                  key={`location-${index}`}
                  locationName={segment.locationName}
                  startTime={segment.startTime}
                  endTime={segment.endTime || weekEnd}
                  weekStart={weekStart}
                  totalMinutes={TOTAL_WEEK_MINUTES}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}

function buildTimelineSegments(
  orders: OrderWithRelations[],
  workOrders: WorkOrder[],
  weekStart: Date,
  weekEnd: Date,
  homeBaseCity: string | null
): TimelineSegment[] {
  const segments: TimelineSegment[] = [];
  
  // Combine orders and work orders into a unified timeline
  interface TimelineItem {
    type: "order" | "work_order";
    startTime: Date;
    endTime: Date;
    order?: OrderWithRelations;
    workOrder?: WorkOrder;
    destinationCity: string;
  }
  
  const timelineItems: TimelineItem[] = [];
  
  // Add regular orders
  for (const order of orders) {
    timelineItems.push({
      type: "order",
      startTime: new Date(order.ready_time),
      endTime: new Date(order.requested_delivery_time_end ?? order.requested_delivery_time),
      order,
      destinationCity: order.ship_to_city,
    });
  }
  
  // Add work orders
  for (const workOrder of workOrders) {
    const startTime = new Date(workOrder.start_time);
    const endTime = addHours(startTime, workOrder.duration_hours);
    timelineItems.push({
      type: "work_order",
      startTime,
      endTime,
      workOrder,
      destinationCity: homeBaseCity || "Unknown", // Work orders return to home base
    });
  }
  
  // Sort by start time
  timelineItems.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  let currentCity: string | null = homeBaseCity;
  let lastEndTime: Date = weekStart;

  for (const item of timelineItems) {
    const isCurrentlyAtHome = currentCity !== null && homeBaseCity !== null && 
      currentCity.toLowerCase() === homeBaseCity.toLowerCase();
    
    if (item.startTime > lastEndTime && currentCity && !isCurrentlyAtHome) {
      segments.push({
        type: "at_location",
        startTime: lastEndTime,
        endTime: item.startTime,
        locationName: currentCity,
        isAtHome: false,
      });
    }

    if (item.type === "order" && item.order) {
      segments.push({
        type: "in_transit",
        startTime: item.startTime,
        endTime: item.endTime,
        order: item.order,
      });
    } else if (item.type === "work_order" && item.workOrder) {
      segments.push({
        type: "work_order",
        startTime: item.startTime,
        endTime: item.endTime,
        workOrder: item.workOrder,
      });
    }

    currentCity = item.destinationCity;
    lastEndTime = item.endTime;
  }

  const isAtHomeAfterOrders = currentCity !== null && homeBaseCity !== null && 
    currentCity.toLowerCase() === homeBaseCity.toLowerCase();
  
  if (currentCity && !isAtHomeAfterOrders && lastEndTime < weekEnd) {
    segments.push({
      type: "at_location",
      startTime: lastEndTime,
      endTime: null,
      locationName: currentCity,
      isAtHome: false,
    });
  }

  return segments;
}
